import axios from 'axios';
import { logError } from '../utils/logger';

export interface ViaCepResponse {
  cep: string;
  logradouro: string;
  complemento: string;
  bairro: string;
  localidade: string;
  uf: string;
  ibge: string;
  gia: string;
  ddd: string;
  siafi: string;
  erro?: boolean;
}

export interface AddressData {
  street: string;
  neighborhood: string;
  city: string;
  state: string;
}

/**
 * Consulta o endereço através do CEP usando a API ViaCEP
 * @param cep - CEP para consulta (apenas números)
 * @returns Dados do endereço ou null em caso de erro
 */
export const fetchAddressByCep = async (cep: string): Promise<AddressData | null> => {
  const cleanCep = cep.replace(/\D/g, '');
  
  if (cleanCep.length !== 8) {
    logError('CEP inválido para consulta', { cep: cleanCep });
    return null;
  }

  const startTime = Date.now();

  try {
    const response = await axios.get<ViaCepResponse>(
      `https://viacep.com.br/ws/${cleanCep}/json/`,
      { timeout: 5000 }
    );

    const duration = Date.now() - startTime;

    if (response.data.erro) {
      logError('CEP não encontrado', { cep: cleanCep, duration_ms: duration });
      return null;
    }

    const addressData: AddressData = {
      street: response.data.logradouro,
      neighborhood: response.data.bairro,
      city: response.data.localidade,
      state: response.data.uf
    };

    return addressData;
  } catch (error: any) {
    const duration = Date.now() - startTime;
    
    logError('Erro ao consultar CEP', {
      cep: cleanCep,
      error: error.message,
      duration_ms: duration
    });

    return null;
  }
};

/**
 * Valida se o CEP está no formato correto
 * @param cep - CEP para validação
 * @returns true se válido, false se inválido
 */
export const isValidCep = (cep: string): boolean => {
  const cleanCep = cep.replace(/\D/g, '');
  return cleanCep.length === 8 && /^\d{8}$/.test(cleanCep);
};

export default {
  fetchAddressByCep,
  isValidCep
};
